import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../services/api';
import { FetchParams } from './employeesSlice';

// Define the TimeEntry interface based on the API documentation
export interface TimeEntry {
  id: number;
  attributes: {
    startTime: string;
    endTime: string | null;
    stage?: {
      data: {
        id: number;
        attributes: {
          name: string;
          order: number;
        }
      }
    };
    employee?: {
      data: {
        id: number;
        attributes: {
          firstName: string;
          lastName: string;
          role: string;
        }
      }
    };
    project?: {
      data: {
        id: number;
        attributes: {
          name: string;
        }
      }
    };
    dress?: {
      data: {
        id: number;
        attributes: {
          Name: string;
          Type: string;
        }
      }
    };
    assignment?: {
      data: {
        id: number;
        attributes: {
          status: string;
        }
      }
    };
    eventType: 'assignment' | 'stage_change' | 'status_change' | 'completion' | 'pause' | 'resume';
    notes: string;
  }
}

interface TimeEntriesState {
  timeEntries: TimeEntry[];
  currentTimeEntry: TimeEntry | null;
  isLoading: boolean;
  error: string | null;
}

const initialState: TimeEntriesState = {
  timeEntries: [],
  currentTimeEntry: null,
  isLoading: false,
  error: null,
};

// Async thunk for fetching time entries
export const fetchTimeEntries = createAsyncThunk(
  'timeEntries/fetchTimeEntries',
  async (params: FetchParams, { rejectWithValue }) => {
    try {
      // Construct query string
      let queryString = '?populate=*';
      if (params.page) queryString += `&pagination[page]=${params.page}`;
      if (params.pageSize) queryString += `&pagination[pageSize]=${params.pageSize}`;
      if (params.sort) queryString += `&sort=${params.sort}`;

      if (params.filters) {
        Object.entries(params.filters).forEach(([key, value]) => {
          if (value) {
            queryString += `&filters[${key}][$eq]=${value}`;
          }
        });
      }

      const response = await api.get(`api/time-entries${queryString}`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error?.message || 'Failed to fetch time entries');
    }
  }
);

// Async thunk for creating a time entry
export const createTimeEntry = createAsyncThunk(
  'timeEntries/createTimeEntry',
  async (entry: {
    startTime: string;
    endTime?: string | null;
    stageId?: number;
    employeeId?: number;
    projectId?: number;
    dressId?: number;
    assignmentId?: number;
    eventType: 'assignment' | 'stage_change' | 'status_change' | 'completion' | 'pause' | 'resume';
    notes?: string;
  }, { rejectWithValue }) => {
    try {
      const requestData = {
        data: {
          startTime: entry.startTime,
          endTime: entry.endTime || null,
          stage: entry.stageId,
          employee: entry.employeeId,
          project: entry.projectId,
          dress: entry.dressId,
          assignment: entry.assignmentId,
          eventType: entry.eventType,
          notes: entry.notes || ''
        }
      };

      const response = await api.post('api/time-entries', requestData);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error?.message || 'Failed to create time entry');
    }
  }
);

// Async thunk for updating a time entry
export const updateTimeEntry = createAsyncThunk(
  'timeEntries/updateTimeEntry',
  async ({ id, entry }: {
    id: number,
    entry: {
      endTime?: string;
      notes?: string;
    }
  }, { rejectWithValue }) => {
    try {
      const requestData = {
        data: {
          ...entry
        }
      };

      const response = await api.put(`api/time-entries/${id}`, requestData);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error?.message || 'Failed to update time entry');
    }
  }
);

// Utility functions for tracking time
export const recordAssignment = (
  assignmentId: number,
  employeeId: number,
  stageId: number,
  dressId: number,
  projectId: number,
  notes = 'Assignment created'
) => {
  return createTimeEntry({
    startTime: new Date().toISOString(),
    assignmentId,
    employeeId,
    stageId,
    dressId,
    projectId,
    eventType: 'assignment',
    notes
  });
};

export const recordStageChange = (
  assignmentId: number,
  employeeId: number,
  stageId: number,
  dressId: number,
  projectId: number,
  notes = 'Stage changed'
) => {
  return createTimeEntry({
    startTime: new Date().toISOString(),
    assignmentId,
    employeeId,
    stageId,
    dressId,
    projectId,
    eventType: 'stage_change',
    notes
  });
};

export const recordStatusChange = (
  assignmentId: number,
  employeeId: number,
  stageId: number,
  dressId: number,
  projectId: number,
  status: string,
  notes = `Status changed to ${status}`
) => {
  return createTimeEntry({
    startTime: new Date().toISOString(),
    assignmentId,
    employeeId,
    stageId,
    dressId,
    projectId,
    eventType: 'status_change',
    notes
  });
};

export const recordCompletion = (
  assignmentId: number,
  employeeId: number,
  stageId: number,
  dressId: number,
  projectId: number
) => {
  return createTimeEntry({
    startTime: new Date().toISOString(),
    assignmentId,
    employeeId,
    stageId,
    dressId,
    projectId,
    eventType: 'completion',
    notes: 'Assignment completed'
  });
};

export const recordPause = (
  assignmentId: number,
  employeeId: number,
  stageId: number,
  dressId: number,
  projectId: number,
  reason: string
) => {
  return createTimeEntry({
    startTime: new Date().toISOString(),
    assignmentId,
    employeeId,
    stageId,
    dressId,
    projectId,
    eventType: 'pause',
    notes: `Assignment paused: ${reason}`
  });
};

export const recordResume = (
  assignmentId: number,
  employeeId: number,
  stageId: number,
  dressId: number,
  projectId: number
) => {
  return createTimeEntry({
    startTime: new Date().toISOString(),
    assignmentId,
    employeeId,
    stageId,
    dressId,
    projectId,
    eventType: 'resume',
    notes: 'Assignment resumed'
  });
};

const timeEntriesSlice = createSlice({
  name: 'timeEntries',
  initialState,
  reducers: {
    clearTimeEntries: (state) => {
      state.timeEntries = [];
      state.currentTimeEntry = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Handle fetchTimeEntries
      .addCase(fetchTimeEntries.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchTimeEntries.fulfilled, (state, action) => {
        state.isLoading = false;
        state.timeEntries = action.payload.data;
      })
      .addCase(fetchTimeEntries.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Handle createTimeEntry
      .addCase(createTimeEntry.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createTimeEntry.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentTimeEntry = action.payload.data;
        state.timeEntries.push(action.payload.data);
      })
      .addCase(createTimeEntry.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Handle updateTimeEntry
      .addCase(updateTimeEntry.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateTimeEntry.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentTimeEntry = action.payload.data;
        
        // Update the time entry in the array
        const index = state.timeEntries.findIndex(entry => entry.id === action.payload.data.id);
        if (index !== -1) {
          state.timeEntries[index] = action.payload.data;
        }
      })
      .addCase(updateTimeEntry.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearTimeEntries } = timeEntriesSlice.actions;
export default timeEntriesSlice.reducer; 